<script setup>
    import { defineProps, defineEmits } from 'vue';
    import { defineExpose } from 'vue';
    import { ref } from 'vue';

    // ----------------------------
    // Props
    // ----------------------------
    const props = defineProps({
        currentPage: {
            type: Number,
            required: true,
        },
        totalPages: {
            type: Number,
            required: true,
        },
    });

    // ----------------------------
    // Emits
    // ----------------------------
    const emit = defineEmits(['pageChanged']);
    const maxPage = ref(0);

    /**
     * 親画面から実行を許可する本コンポーネント内のメソッドの一覧。
     */
    defineExpose({
        onload,
    });

    // 一覧取得API実行時に取得した最大件数から、ページング座標を計算する。
    async function onload(totalPage) {
        maxPage.value = Math.ceil(totalPage / 50);
        pagination();
    }

    const paginationRange = ref([]);

    function pagination() {
        const pages = [];

        switch (maxPage.value) {
            case 1:
            case 2:
                // 合計ページ数が1ページ、2ページの場合は配列を作成しない。
                break;
            case 3:
                // 合計ページ数が3ページの場合。
                pages.push(2);
                break;
            case 4:
                // 合計ページ数が4ページの場合。
                pages.push(2);
                pages.push(3);
                break;
            case 5:
                // 合計ページ数が5ページの場合。
                pages.push(2);
                pages.push(3);
                pages.push(4);
                break;
            case 6:
                // 合計ページ数が6ページの場合。
                pages.push(2);
                pages.push(3);
                pages.push(4);
                pages.push(5);
                break;
            default:
                // 合計ページ数が7ページ以上の場合。
                // 現在のページ座標に応じて、ページ番号を切り替える。
                switch (props.currentPage) {
                    // 1ページ目が選択された場合。
                    case 1:
                        pages.push(props.currentPage + 1);
                        pages.push(props.currentPage + 2);
                        pages.push(props.currentPage + 3);
                        pages.push(props.currentPage + 4);
                        pages.push(props.currentPage + 5);
                        break;
                    // 2ページ目が選択された場合。
                    case 2:
                        pages.push(props.currentPage);
                        pages.push(props.currentPage + 1);
                        pages.push(props.currentPage + 2);
                        pages.push(props.currentPage + 3);
                        pages.push(props.currentPage + 4);
                        break;
                    // 3ページ目が選択された場合。
                    case 3:
                        pages.push(props.currentPage - 1);
                        pages.push(props.currentPage);
                        pages.push(props.currentPage + 1);
                        pages.push(props.currentPage + 2);
                        pages.push(props.currentPage + 3);
                        break;
                    // 4ページ目が選択された場合。
                    case 4:
                        pages.push(props.currentPage - 2);
                        pages.push(props.currentPage - 1);
                        pages.push(props.currentPage);
                        pages.push(props.currentPage + 1);
                        pages.push(props.currentPage + 2);
                        break;
                    // 最終ページが選択された場合。
                    case maxPage.value:
                        pages.push(props.currentPage - 5);
                        pages.push(props.currentPage - 4);
                        pages.push(props.currentPage - 3);
                        pages.push(props.currentPage - 2);
                        pages.push(props.currentPage - 1);
                        break;
                    // 最終ページから1ページ前が選択された場合。
                    case maxPage.value - 1:
                        pages.push(props.currentPage - 4);
                        pages.push(props.currentPage - 3);
                        pages.push(props.currentPage - 2);
                        pages.push(props.currentPage - 1);
                        pages.push(props.currentPage);
                        break;
                    // 最終ページから2ページ前が選択された場合。
                    case maxPage.value - 2:
                        pages.push(props.currentPage - 3);
                        pages.push(props.currentPage - 2);
                        pages.push(props.currentPage - 1);
                        pages.push(props.currentPage);
                        pages.push(props.currentPage + 1);
                        break;
                    // 最終ページから3ページ前が選択された場合。
                    case maxPage.value - 3:
                        pages.push(props.currentPage - 2);
                        pages.push(props.currentPage - 1);
                        pages.push(props.currentPage);
                        pages.push(props.currentPage + 1);
                        pages.push(props.currentPage + 2);
                        break;
                    // 上記のケース以外のページが選択された場合。
                    default:
                        pages.push(props.currentPage - 2);
                        pages.push(props.currentPage - 1);
                        pages.push(props.currentPage);
                        pages.push(props.currentPage + 1);
                        pages.push(props.currentPage + 2);
                        break;
                }
                break;
        }
        paginationRange.value = pages;
    }

    // ----------------------------
    // 関数
    // ----------------------------
    function changePage(page) {
        if (page > 0 && page <= maxPage.value) {
            // 親コンポーネントに選択されたページを返す
            emit('pageChanged', page);
        }
    }
</script>

<template>
    <ul class="paging">
        <li @click="changePage(props.currentPage - 1)">
            <button type="button" class="pagingBtn">&nbsp;</button>
        </li>
        <li @click="changePage(1)">
            <button type="button" :class="{ pagingBtn: true, selected: props.currentPage === 1 }">1</button>
        </li>
        <li v-if="props.currentPage > 4 && maxPage > 7">
            <button type="button" class="pagingBtn">...</button>
        </li>
        <template v-if="maxPage > 1">
            <li v-for="page in paginationRange" :key="page" @click="changePage(page)">
                <button type="button" :class="{ pagingBtn: true, selected: props.currentPage === page }">{{ page }}</button>
            </li>
        </template>
        <li v-if="maxPage > 7 && maxPage - 3 > props.currentPage">
            <button type="button" class="pagingBtn">...</button>
        </li>
        <li @click="changePage(maxPage)" v-if="maxPage > 1">
            <button type="button" :class="{ pagingBtn: true, selected: props.currentPage === maxPage }">{{ maxPage }}</button>
        </li>
        <li @click="changePage(props.currentPage + 1)">
            <button type="button" class="pagingBtn">&nbsp;</button>
        </li>
    </ul>
</template>

<style></style>